import { ReactElement, useCallback, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  Text,
  useDisclosure,
  useBreakpointValue,
  useToast,
  VStack,
  ModalFooter,
  UseDisclosureProps,
} from "@chakra-ui/react";

import EmailVerificationForm from "./EmailVerificationForm";
import UserPermissionForm from "./UserPermissionForm";
import ConfirmationModal, { ConfirmationModalProps } from "./ConfirmationModal";

import { useCustomerContext } from "contexts/CustomerProvider";
import PrimaryButton from "components/PrimaryButton";
import useInviteExistingUser, {
  useCreateNewUser,
  useVerifyEmail,
} from "hooks/api/customers/useInviteUser";
import { FormProvider, useForm, SubmitHandler } from "react-hook-form";
import { SchemaValidationError } from "services/apiErrors";
import { permissionKeyToPermissionValue } from "services/customerPermissionsHelpers";

export type InviteNewUserInputProps = UserRequestDetails & UserPermissionsFlags;
type ValidErrorKeys = keyof Pick<
  InviteNewUserInputProps,
  | "email"
  | "firstName"
  | "lastName"
  | "displayName"
  | "phoneNumber"
  | "readAccess"
  | "addEditOrders"
  | "signContracts"
  | "userManagement"
>;

export type ExistingUserType = string | null | undefined;

const emailSchema = yup.object({
  email: yup
    .string()
    .email("Must be a valid email")
    .required("Email is required"),
});

const userNameSchema = emailSchema.shape({
  firstName: yup
    .string()
    .max(64, "First Name must be ${max} characters or less")
    .required("First Name is required"),
  lastName: yup
    .string()
    .max(64, "Last Name must be ${max} characters or less")
    .required("Last Name is required"),
  phoneNumber: yup
    .string()
    .max(30, "Phone # must be ${max} characters or less")
    .nullable(),
  readAccess: yup.bool().notRequired(),
  addEditOrders: yup.bool().notRequired(),
  signContracts: yup.bool().notRequired(),
  userManagement: yup.bool().notRequired(),
});

const UserExistsInPortal = ({
  email,
  onClose,
}: {
  email: ExistingUserType;
  onClose: () => void;
}) => {
  return (
    <VStack spacing={4} display="left" h="100%" p={4}>
      <Text textStyle="h4" fontSize="16px" p={6} pb={0}>
        {email} already exists in the Customer Portal.
      </Text>
      <ModalFooter>
        <PrimaryButton mr={3} onClick={onClose}>
          Close
        </PrimaryButton>
      </ModalFooter>
    </VStack>
  );
};

const FormSteps = ({
  userId,
  step,
  handleEmailVerificationSubmit,
  handleUserPermissionSubmit,
  confirmationModalProps,
  onClose,
}: {
  userId: ExistingUserType;
  step: number;
  handleEmailVerificationSubmit?: React.FormEventHandler<HTMLFormElement>;
  handleUserPermissionSubmit?: React.FormEventHandler<HTMLFormElement>;
  confirmationModalProps: Omit<ConfirmationModalProps, "onClose">;
  onClose: () => void;
}) => {
  switch (step) {
    case 1:
      return (
        <form onSubmit={handleEmailVerificationSubmit}>
          <EmailVerificationForm onClose={onClose} />
        </form>
      );
    case 2:
      return (
        <form onSubmit={handleUserPermissionSubmit}>
          <UserPermissionForm userId={userId} onClose={onClose} />
        </form>
      );
    case 3:
      return (
        <ConfirmationModal {...confirmationModalProps} onClose={onClose} />
      );
    default:
      return <Text>I believe something went wrong</Text>;
  }
};

const InviteNewUserModal = ({
  onClose = () => {
    return;
  },
}: Pick<UseDisclosureProps, "onClose">): ReactElement => {
  const modalSize = useBreakpointValue({ base: "xs", md: "xl" });

  const toast = useToast();

  const [newUserName, setNewUserName] = useState<string>();
  const [step, setStep] = useState(1);

  const { selectedCustomers, selectedCustomerCodes } = useCustomerContext();

  const { mutateAsync: verifyEmailExists, data: userData } = useVerifyEmail(
    selectedCustomerCodes[0]
  );
  const hasUserId = userData?.value?.[0]?.UserId;

  const methods = useForm<InviteNewUserInputProps>({
    defaultValues: {
      email: undefined,
      firstName: "",
      lastName: "",
      phoneNumber: "",
      readAccess: true,
      displayName: "",
    },
    // The schema type is variable and getting it to match the form's type is non-trivial
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    resolver: yupResolver(step > 1 ? userNameSchema : emailSchema),
  });
  const { handleSubmit, setError, setValue } = methods;

  const { mutateAsync: createNewUser } = useCreateNewUser();

  const { mutateAsync: inviteNewUser } = useInviteExistingUser(
    hasUserId ?? undefined
  );

  const existingUserName = userData?.value?.[0]?.Name;

  const nextStep = useCallback(() => {
    setStep(step + 1);
  }, [step]);

  const checkEmail: SubmitHandler<InviteNewUserInputProps> = useCallback(
    (data) => {
      verifyEmailExists(data.email)
        .then((response) => {
          if (response.totalRows >= 1 && response.value?.[0]) {
            setValue("firstName", response.value[0].FirstName ?? "");
            setValue("lastName", response.value[0].LastName ?? "");
            setValue("phoneNumber", response.value[0].Phone ?? "");
            setValue("displayName", response.value[0].Name ?? "");
          }
          nextStep();
        })
        .catch((err) => {
          toast({
            title: "Error",
            description: `Unable to verify email: ${err.message}`,
            status: "error",
            duration: 3000,
            isClosable: true,
            position: "top",
          });
          onClose();
        });
    },
    [onClose, nextStep, setValue, toast, verifyEmailExists]
  );

  const onSubmitInviteExistingUser: SubmitHandler<InviteNewUserInputProps> =
    useCallback(
      (data: InviteNewUserInputProps) => {
        return inviteNewUser({
          userDetails: {
            ...data,
            cardCode: selectedCustomerCodes[0],
          },
          permissionFlags: {
            readAccess: true,
            signContracts: data.signContracts,
            addEditOrders: data.addEditOrders,
            userManagement: data.userManagement,
            requestContract: data.requestContract,
          },
        })
          .then(nextStep)
          .catch((err: Error) => {
            if (err instanceof SchemaValidationError) {
              const propertyNameToLabelMap: Record<ValidErrorKeys, string> = {
                email: "Email",
                firstName: "FirstName",
                lastName: "LastName",
                phoneNumber: "PhoneNumber",
                displayName: "Name",
                ...permissionKeyToPermissionValue,
              };
              err.errors?.forEach(({ propertyName, message }) => {
                if (
                  propertyName &&
                  propertyNameToLabelMap.hasOwnProperty(propertyName)
                ) {
                  setError(propertyName as ValidErrorKeys, {
                    message: `${
                      propertyNameToLabelMap[propertyName as ValidErrorKeys]
                    } ${message}`,
                    type: "manual",
                  });
                }
              });
            }
            toast({
              title: "Error",
              description: "Failed to invite new user, please try again",
              status: "error",
              duration: 3000,
              isClosable: true,
              position: "top",
            });
          });
      },
      [inviteNewUser, nextStep, selectedCustomerCodes, setError, toast]
    );

  const onSubmitCreateNewUser: SubmitHandler<InviteNewUserInputProps> =
    useCallback(
      (data: InviteNewUserInputProps) => {
        return createNewUser({
          userDetails: {
            ...data,
            cardCode: selectedCustomerCodes[0],
          },
          permissionFlags: {
            readAccess: true,
            addEditOrders: data.addEditOrders,
            signContracts: data.signContracts,
            userManagement: data.userManagement,
            requestContract: data.requestContract,
          },
        })
          .then(() => {
            nextStep();
            setNewUserName(`${data.firstName} ${data.lastName}`);
          })
          .catch((err: Error) => {
            toast({
              title: `${err}`,
              description: "Failed to invite new user, please try again",
              status: "error",
              duration: 3000,
              isClosable: true,
              position: "top",
            });
          });
      },
      [createNewUser, nextStep, selectedCustomerCodes, toast]
    );

  return (
    <FormProvider {...methods}>
      <Modal
        isOpen={true}
        onClose={onClose}
        isCentered
        motionPreset="slideInBottom"
        size={modalSize}
      >
        <ModalOverlay />
        <ModalContent>
          {userData?.value?.[0]?.CardCode ? (
            <UserExistsInPortal
              email={userData?.value?.[0]?.Email}
              onClose={onClose}
            />
          ) : (
            <>
              <VStack spacing={4} display="left" h="100%">
                <Text textStyle="h4" fontSize="18px" p={6} pb={0}>
                  {selectedCustomers[0]?.name}
                </Text>
                {step === 3 ? undefined : (
                  <ModalHeader
                    p={6}
                    style={{
                      paddingTop: "14px",
                      paddingBottom: "0",
                      marginTop: "0",
                    }}
                  >
                    Invite New User
                  </ModalHeader>
                )}
              </VStack>
              <ModalCloseButton />
              <FormSteps
                userId={hasUserId}
                step={step}
                handleEmailVerificationSubmit={handleSubmit(checkEmail)}
                handleUserPermissionSubmit={handleSubmit(
                  !!hasUserId
                    ? onSubmitInviteExistingUser
                    : onSubmitCreateNewUser
                )}
                confirmationModalProps={{
                  existingUserName: existingUserName,
                  newUserName: newUserName,
                }}
                onClose={onClose}
              />
            </>
          )}
        </ModalContent>
      </Modal>
    </FormProvider>
  );
};

export const InviteUserButton = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  return (
    <>
      <PrimaryButton onClick={onOpen}>Invite User</PrimaryButton>
      {isOpen ? <InviteNewUserModal onClose={onClose} /> : null}
    </>
  );
};

export default InviteNewUserModal;
